import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Check, MapPin, Globe, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PROMO_WORDS = [
  'buy now', 'limited time', "don't miss", 'act fast', 'order now', 'sale ends',
  'best deal', 'exclusive offer', 'discount', 'free trial', 'sign up now',
  'shop now', 'save big', 'hurry', 'only today', 'special offer',
];

const PRESENCE_OPTIONS = [
  { id: 'base_camp', label: 'Base Camp', desc: 'Rotating placard among activities' },
  { id: 'map', label: 'Map', desc: 'Gold pin showing your location' },
  { id: 'activity_detail', label: 'Activity Detail', desc: '"Made possible by" line on activities' },
];

export default function BecomeSupporter() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    business_name: '',
    description: '',
    logo_url: '',
    location: '',
    website: '',
    presence_preferences: ['base_camp'],
  });
  const [errors, setErrors] = useState({});

  if (!user) {
    navigate('/auth');
    return null;
  }

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const togglePresence = (id) => {
    setForm(prev => ({
      ...prev,
      presence_preferences: prev.presence_preferences.includes(id)
        ? prev.presence_preferences.filter(p => p !== id)
        : [...prev.presence_preferences, id]
    }));
  };

  const validateSetup = () => {
    const e = {};
    if (!form.business_name.trim() || form.business_name.trim().length < 2) e.business_name = 'Business name is required';
    if (!form.description.trim() || form.description.trim().length < 10) e.description = 'Add a short neutral description (at least 10 characters)';
    const descLower = (form.business_name + ' ' + form.description).toLowerCase();
    for (const word of PROMO_WORDS) {
      if (descLower.includes(word)) {
        e.description = `Please use neutral language. Avoid "${word}".`;
        break;
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        business_name: form.business_name.trim(),
        description: form.description.trim(),
        logo_url: form.logo_url.trim() || null,
        location: form.location.trim() || null,
        website: form.website.trim() || null,
        presence_preferences: form.presence_preferences,
      };
      await axios.post(`${API}/api/sponsors/become-supporter`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStep(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-lg mx-auto px-4 flex items-center h-14 gap-3">
          <button onClick={() => step > 1 && step < 5 ? setStep(step - 1) : navigate(-1)} className="p-2 rounded-full hover:opacity-70" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
          </button>
          <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Become a Supporter</h1>
        </div>
        {/* Progress */}
        <div className="max-w-lg mx-auto px-4 pb-2">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(s => (
              <div key={s} className="flex-1 h-1 rounded-full" style={{ backgroundColor: s <= step ? 'var(--lk-sage)' : 'var(--fy-border-subtle)' }} />
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">

        {/* STEP 1 — Invitation */}
        {step === 1 && (
          <div className="text-center" data-testid="step-invitation">
            <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--fy-text-primary)' }}>
              Support activity happening around you
            </h2>
            <p className="text-sm mb-8" style={{ color: 'var(--fy-text-secondary)', lineHeight: 1.7 }}>
              LikeKind helps people get together in real life.
              Local supporters make that possible.
            </p>
            <Button
              onClick={() => setStep(2)}
              className="h-11 px-8 rounded-full font-semibold"
              style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
              data-testid="step1-continue"
            >
              Continue
            </Button>
          </div>
        )}

        {/* STEP 2 — What This Means */}
        {step === 2 && (
          <div className="text-center" data-testid="step-explanation">
            <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--fy-text-primary)' }}>
              What this means
            </h2>
            <p className="text-sm mb-8" style={{ color: 'var(--fy-text-secondary)', lineHeight: 1.7 }}>
              Your business will appear as a supporter across the platform —
              on activities, the map, and Base Camp.
            </p>
            <p className="text-sm mb-8" style={{ color: 'var(--fy-text-secondary)', lineHeight: 1.7 }}>
              No ads. No interruptions. Just visible support.
            </p>
            <Button
              onClick={() => setStep(3)}
              className="h-11 px-8 rounded-full font-semibold"
              style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
              data-testid="step2-continue"
            >
              Continue
            </Button>
          </div>
        )}

        {/* STEP 3 — Setup */}
        {step === 3 && (
          <div data-testid="step-setup">
            <h2 className="text-xl font-bold mb-5" style={{ color: 'var(--fy-text-primary)' }}>
              About your business
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                  Business name <span style={{ color: 'var(--lk-terra)' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.business_name}
                  onChange={e => handleChange('business_name', e.target.value)}
                  placeholder="Your business name"
                  maxLength={100}
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{ backgroundColor: 'var(--fy-surface-card)', border: `1px solid ${errors.business_name ? 'var(--lk-terra)' : 'var(--fy-border-subtle)'}`, color: 'var(--fy-text-primary)' }}
                  data-testid="sponsor-name-input"
                />
                {errors.business_name && <p className="text-xs mt-1" style={{ color: 'var(--lk-terra)' }}>{errors.business_name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                  Short description <span style={{ color: 'var(--lk-terra)' }}>*</span>
                </label>
                <textarea
                  value={form.description}
                  onChange={e => handleChange('description', e.target.value)}
                  placeholder="A neutral description of your business (1-2 lines)"
                  rows={2}
                  maxLength={240}
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none"
                  style={{ backgroundColor: 'var(--fy-surface-card)', border: `1px solid ${errors.description ? 'var(--lk-terra)' : 'var(--fy-border-subtle)'}`, color: 'var(--fy-text-primary)' }}
                  data-testid="sponsor-description-input"
                />
                <p className="text-xs mt-1" style={{ color: errors.description ? 'var(--lk-terra)' : 'var(--fy-text-disabled)' }}>
                  {errors.description || 'Keep it neutral. No promotional language.'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                  <MapPin className="w-3.5 h-3.5 inline mr-1" /> Location
                </label>
                <input
                  type="text"
                  value={form.location}
                  onChange={e => handleChange('location', e.target.value)}
                  placeholder="City or area (optional)"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                  data-testid="sponsor-location-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                  <Globe className="w-3.5 h-3.5 inline mr-1" /> Website
                </label>
                <input
                  type="url"
                  value={form.website}
                  onChange={e => handleChange('website', e.target.value)}
                  placeholder="https://... (optional)"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                  data-testid="sponsor-website-input"
                />
              </div>
            </div>

            <Button
              onClick={() => { if (validateSetup()) setStep(4); }}
              className="w-full h-11 rounded-full font-semibold mt-6"
              style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
              data-testid="step3-continue"
            >
              Continue
            </Button>
          </div>
        )}

        {/* STEP 4 — Presence Selection */}
        {step === 4 && (
          <div data-testid="step-presence">
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>
              Where would you like to show your support?
            </h2>
            <p className="text-sm mb-5" style={{ color: 'var(--fy-text-disabled)' }}>
              These are preferences. Rotation controls actual visibility.
            </p>
            <div className="space-y-2">
              {PRESENCE_OPTIONS.map(opt => (
                <Card
                  key={opt.id}
                  className="p-4 cursor-pointer transition-all active:scale-[0.98]"
                  style={{
                    backgroundColor: form.presence_preferences.includes(opt.id) ? 'var(--lk-sage-muted, #EDF1E8)' : 'var(--fy-surface-card)',
                    border: `2px solid ${form.presence_preferences.includes(opt.id) ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
                  }}
                  onClick={() => togglePresence(opt.id)}
                  data-testid={`presence-${opt.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>{opt.label}</p>
                      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{opt.desc}</p>
                    </div>
                    {form.presence_preferences.includes(opt.id) && (
                      <Check className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                    )}
                  </div>
                </Card>
              ))}
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting || form.presence_preferences.length === 0}
              className="w-full h-11 rounded-full font-semibold mt-6"
              style={{ backgroundColor: 'var(--lk-sage)', color: '#fff', opacity: submitting ? 0.6 : 1 }}
              data-testid="step4-submit"
            >
              {submitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          </div>
        )}

        {/* STEP 5 — Confirmation */}
        {step === 5 && (
          <div className="text-center py-8" data-testid="step-confirmation">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'var(--lk-sage-muted, #EDF1E8)' }}>
              <Check className="w-8 h-8" style={{ color: 'var(--lk-sage)' }} />
            </div>
            <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--fy-text-primary)' }}>
              Application submitted
            </h2>
            <p className="text-sm mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
              You're on your way to supporting activity in your area.
            </p>
            <p className="text-xs mb-8" style={{ color: 'var(--fy-text-disabled)' }}>
              Your presence will begin after review. This usually takes a short while.
            </p>
            <Button
              onClick={() => navigate('/base-camp')}
              className="h-11 px-8 rounded-full font-semibold"
              style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
              data-testid="step5-done"
            >
              Back to Base Camp
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
